/**
 * Embudo del cliente en la web de propinas, medido por nosotros (no por GA).
 *
 * Cada visita tiene un id de sesión aleatorio que vive solo en la pestaña
 * (sessionStorage). Se envía un evento por paso: página cargada, importe
 * elegido, pago iniciado, pago OK, pago fallido y abandono (cierra o deja la
 * pestaña sin iniciar el pago). Sin IP, sin cookies, sin datos personales:
 * solo el negocio, el canal, el paso, el tiempo desde la carga, el tipo de
 * móvil y el idioma. Va con navigator.sendBeacon para que llegue aunque la
 * pestaña se cierre.
 */
import { API } from "../api";
import { detectarDispositivo } from "./capturaPropina";

const CLAVE = "tipify_embudo";
const cargaAt = Date.now();

function leer() {
  try {
    return JSON.parse(sessionStorage.getItem(CLAVE) || "null") || null;
  } catch {
    return null;
  }
}

function guardar(estado) {
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify(estado));
  } catch {
    // sin almacenamiento: se mide igual, sin deduplicar
  }
}

function nuevoId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "").slice(0, 24);
  } catch {
    // sin crypto
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function enviar(evento) {
  const url = `${API}/embudo`;
  const cuerpo = JSON.stringify([evento]);
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([cuerpo], { type: "text/plain" }))) return;
  } catch {
    // sendBeacon no disponible o rechazado: fetch
  }
  try {
    fetch(url, { method: "POST", headers: { "Content-Type": "text/plain" }, body: cuerpo, keepalive: true, credentials: "omit" }).catch(() => {});
  } catch {
    // sin red: no pasa nada
  }
}

/**
 * Primer paso de la visita: fija el negocio y el canal de la sesión. Si la
 * pestaña ya traía otra sesión de otro negocio, empieza una nueva.
 * @param {{ negocio: string, canal: 'qr_negocio'|'qr_empleado'|'evento'|'cuenta_mesa' }} ctx
 */
export function paginaCargada({ negocio, canal }) {
  if (!negocio) return;
  let estado = leer();
  if (!estado || estado.negocio !== String(negocio)) {
    estado = { sesion: nuevoId(), negocio: String(negocio), canal: canal || "otro", pasos: [] };
    guardar(estado);
  }
  registrar("pagina_cargada");
}

/**
 * Registra un paso de la sesión actual. Los pasos únicos no se repiten.
 * @param {string} paso
 * @param {{ tipId?: string, metodo?: string, negocio?: string }} [extra]
 */
export function registrar(paso, extra = {}) {
  let estado = leer();
  if (!estado && extra.negocio) {
    // Pantallas posteriores (éxito) abiertas sin pasar por el formulario en esta pestaña
    estado = { sesion: nuevoId(), negocio: String(extra.negocio), canal: "otro", pasos: [] };
  }
  if (!estado) return;
  const unico = paso !== "pago_fallido";
  if (unico && estado.pasos.includes(paso)) return;
  if (unico) {
    estado.pasos.push(paso);
    guardar(estado);
  }
  enviar({
    sesion: estado.sesion,
    negocio: estado.negocio,
    canal: estado.canal,
    paso,
    msDesdeCarga: Date.now() - cargaAt,
    dispositivo: detectarDispositivo(),
    idioma: (typeof navigator !== "undefined" ? navigator.language || "" : "").split("-")[0].toLowerCase(),
    tipId: extra.tipId || undefined,
    metodo: extra.metodo || undefined,
  });
}

/**
 * Abandono: la pestaña se oculta o se cierra sin haber iniciado el pago.
 * Devuelve la función para quitar el oyente.
 */
export function vigilarAbandono() {
  if (typeof document === "undefined") return () => {};
  const alOcultar = () => {
    if (document.visibilityState !== "hidden") return;
    const estado = leer();
    if (!estado || estado.pasos.includes("pago_iniciado") || estado.pasos.includes("pago_ok")) return;
    registrar("abandono");
  };
  document.addEventListener("visibilitychange", alOcultar);
  window.addEventListener("pagehide", alOcultar);
  return () => {
    document.removeEventListener("visibilitychange", alOcultar);
    window.removeEventListener("pagehide", alOcultar);
  };
}
